import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Key, Settings, Compass, Lightbulb, Coins, Waves } from 'lucide-react';

const BenefitCard = ({ benefit, index, delay }) => {
  const IconComponent = benefit.icon;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: delay + index * 0.1 }}
      className="group"
    >
      <div className="card-elevated h-full p-6 flex flex-col">
        <div className="icon-circle mb-4 group-hover:scale-110 transition-transform duration-300">
          <IconComponent size={24} />
        </div>
        <h3 className="text-lg font-serif font-semibold text-foreground mb-2">
          {benefit.title}
        </h3>
        <p className="text-sm text-muted-foreground leading-relaxed flex-1">
          {benefit.description}
        </p>
      </div>
    </motion.div>
  );
};

export const BenefitsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const { t } = useTranslation();

  const businessBenefits = [
    {
      icon: Key,
      title: t('benefits.business.access.title'),
      description: t('benefits.business.access.description')
    },
    {
      icon: Settings,
      title: t('benefits.business.efficiency.title'),
      description: t('benefits.business.efficiency.description')
    },
    {
      icon: Compass,
      title: t('benefits.business.continuity.title'),
      description: t('benefits.business.continuity.description')
    }
  ];

  const retireeBenefits = [
    {
      icon: Lightbulb,
      title: t('benefits.retiree.active.title'),
      description: t('benefits.retiree.active.description')
    },
    {
      icon: Coins,
      title: t('benefits.retiree.income.title'),
      description: t('benefits.retiree.income.description')
    },
    {
      icon: Waves,
      title: t('benefits.retiree.impact.title'),
      description: t('benefits.retiree.impact.description')
    }
  ];

  return (
    <section ref={ref} className="py-20 lg:py-28">
      {/* For Businesses */}
      <div className="bg-secondary/30 py-20">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <span className="inline-block px-4 py-1.5 bg-primary/10 text-primary text-sm font-medium rounded-full mb-4">
              {t('benefits.business.badge')}
            </span>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-foreground mb-4">
              {t('benefits.business.title')}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t('benefits.business.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {businessBenefits.map((benefit, index) => (
              <BenefitCard key={benefit.title} benefit={benefit} index={index} delay={0.2} />
            ))}
          </div>
        </div>
      </div>

      {/* For Retirees */}
      <div className="py-20">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <span className="inline-block px-4 py-1.5 bg-primary/10 text-primary text-sm font-medium rounded-full mb-4">
              {t('benefits.retiree.badge')}
            </span>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-foreground mb-4">
              {t('benefits.retiree.title')}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t('benefits.retiree.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {retireeBenefits.map((benefit, index) => (
              <BenefitCard key={benefit.title} benefit={benefit} index={index} delay={0.2} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
